import React, { useState, useEffect, useRef } from "react";
import Hls from "hls.js";
import {
  FaPlay,
  FaPause,
  FaVolumeHigh,
  FaVolumeXmark,
  FaExpand,
  FaCompress,
  FaXmark,
  FaFilm,
  FaRotateLeft,
  FaRotateRight,
  FaGear
} from "react-icons/fa6";
import "./ShortFilmModal.css";

const getYouTubeEmbedUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0&enablejsapi=1` : null;
};

const masterHls = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/hls/master.m3u8` : "/hls/master.m3u8";
const defaultHls = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/hls/output.m3u8` : "/hls/output.m3u8";

const getQualityLabel = (height) => {
  if (!height) return "Auto HD";
  if (height >= 2160) return `${height}p (4K Ultra HD)`;
  if (height >= 1080) return `${height}p (1080p Full HD)`;
  if (height >= 720) return `${height}p (720p HD)`;
  return `${height}p`;
};

const ShortFilmModal = ({
  isOpen = true,
  onClose,
  videoTitle = "ANDAM ENVASAM - Short Film",
  videoUrl,
  hlsSource = masterHls
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hlsRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Check if any source is YouTube
  const youtubeEmbedUrl = getYouTubeEmbedUrl(videoUrl) || getYouTubeEmbedUrl(hlsSource);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [qualityLevels, setQualityLevels] = useState([]);
  const [selectedQuality, setSelectedQuality] = useState(-1); // -1 is Auto ABR
  const [videoError, setVideoError] = useState(false);

  const triggerShowControls = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3500);
    }
  };

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, []);

  // Initialize Video & Autoplay with Unmute
  useEffect(() => {
    if (!isOpen) return;

    setVideoError(false);
    setIsBuffering(true);

    const video = videoRef.current;
    if (!video) return;

    let hlsInstance = null;
    let loadingTimeout = null;

    const startPlayback = () => {
      // Start muted by default to prevent mobile browser autoplay permission prompts
      video.muted = true;
      setIsMuted(true);
      setAutoplayBlocked(true);

      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            setIsBuffering(false);
            setVideoError(false);
          })
          .catch((error) => {
            console.warn("Autoplay was restricted by browser:", error);
            setIsBuffering(false);
          });
      }
    };

    // Video buffering listeners
    const handleWaiting = () => setIsBuffering(true);
    const handlePlaying = () => {
      setIsBuffering(false);
      setVideoError(false);
      if (loadingTimeout) clearTimeout(loadingTimeout);
    };
    const handleCanPlay = () => {
      setIsBuffering(false);
      setVideoError(false);
      if (loadingTimeout) clearTimeout(loadingTimeout);
    };
    const handleError = () => {
      console.error("Direct video source error on video element");
      setIsBuffering(false);
      setVideoError(true);
      setIsPlaying(false);
      if (loadingTimeout) clearTimeout(loadingTimeout);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      handleClose();
    };

    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("playing", handlePlaying);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("error", handleError);
    video.addEventListener("ended", handleEnded);

    // Timeout safety: if video hangs loading for >10s, stop spinner
    loadingTimeout = setTimeout(() => {
      if (video && (video.readyState < 3)) {
        console.warn("Video loading timed out or failed to buffer.");
        setIsBuffering(false);
        setVideoError(true);
      }
    }, 10000);

    // Check if source is HLS (.m3u8)
    const isHls = hlsSource && (hlsSource.endsWith(".m3u8") || hlsSource.includes("/hls/"));

    if (isHls && Hls.isSupported()) {
      hlsInstance = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        startFragPrefetch: true,
        maxBufferLength: 15,
        maxMaxBufferLength: 30,
        maxBufferSize: 30 * 1024 * 1024,
        maxBufferHole: 0.8,
        highBufferWatchdogPeriod: 2,
        nudgeMaxRetry: 5,
      });
      hlsInstance.loadSource(hlsSource);
      hlsInstance.attachMedia(video);
      hlsRef.current = hlsInstance;

      hlsInstance.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        if (data && data.levels) {
          const levels = data.levels.map((lvl, index) => ({
            id: index,
            height: lvl.height || 1080,
            bitrate: lvl.bitrate,
          }));
          // Sort levels from highest resolution to lowest resolution (4K -> 1080p -> 720p -> 480p -> 360p)
          levels.sort((a, b) => b.height - a.height);
          setQualityLevels(levels);
        }
        startPlayback();
      });

      hlsInstance.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          if (hlsSource.includes("master.m3u8")) {
            console.warn("Master HLS not found, falling back to output.m3u8:", defaultHls);
            hlsInstance.loadSource(defaultHls);
          } else {
            console.error("Fatal HLS stream error:", data);
            setVideoError(true);
            setIsBuffering(false);
            setIsPlaying(false);
          }
        }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Native Safari HLS support
      video.src = hlsSource;
      video.addEventListener("loadedmetadata", startPlayback);
    } else {
      console.warn("HLS streaming is not supported on this browser.");
      setVideoError(true);
      setIsBuffering(false);
    }

    // ESC Key to close modal
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !document.fullscreenElement) {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      if (hlsInstance) {
        hlsInstance.destroy();
      }
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("error", handleError);
      video.removeEventListener("ended", handleEnded);
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hlsSource]);

  // Sync Video Time Updates
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setDuration(videoRef.current.duration || 0);
    }
  };

  // Toggle Play / Pause
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (autoplayBlocked) {
      handleUnmute();
      return;
    }
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  // Unmute Action
  const handleUnmute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = false;
    videoRef.current.volume = volume || 1;
    setIsMuted(false);
    setAutoplayBlocked(false);
    if (!isPlaying) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  // Toggle Mute
  const toggleMute = () => {
    if (!videoRef.current) return;
    const nextMuted = !isMuted;
    videoRef.current.muted = nextMuted;
    setIsMuted(nextMuted);
    if (autoplayBlocked && !nextMuted) {
      setAutoplayBlocked(false);
    }
  };

  // Volume Change
  const handleVolumeChange = (e) => {
    const newVol = parseFloat(e.target.value);
    setVolume(newVol);
    if (videoRef.current) {
      videoRef.current.volume = newVol;
      videoRef.current.muted = newVol === 0;
      setIsMuted(newVol === 0);
    }
  };

  // Seek Timeline
  const handleSeek = (e) => {
    const seekTime = parseFloat(e.target.value);
    setCurrentTime(seekTime);
    if (videoRef.current) {
      videoRef.current.currentTime = seekTime;
    }
  };

  // Skip Backward / Forward (-10s / +10s)
  const skipTime = (seconds) => {
    if (!videoRef.current) return;
    const newTime = Math.min(Math.max(videoRef.current.currentTime + seconds, 0), duration);
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Change Playback Speed (0.5x, 1x, 1.25x, 1.5x, 2x)
  const changeSpeed = (speed) => {
    setPlaybackSpeed(speed);
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
    }
    setShowSpeedMenu(false);
  };

  // Change HLS Video Quality (Auto, 1080p, 720p, 480p, 360p)
  const changeQuality = (levelId) => {
    setSelectedQuality(levelId);
    try {
      if (hlsRef.current && hlsRef.current.levels && hlsRef.current.levels.length > 0) {
        if (levelId === -1 || (typeof levelId === "number" && levelId >= 0 && levelId < hlsRef.current.levels.length)) {
          hlsRef.current.currentLevel = levelId;
        }
      }
    } catch (err) {
      console.warn("Quality switch handled safely:", err);
    }
    setShowQualityMenu(false);
  };

  // Lock orientation to landscape when entering fullscreen (only if explicitly supported)
  const lockLandscapeOrientation = () => {
    try {
      if (
        window.screen &&
        window.screen.orientation &&
        typeof window.screen.orientation.lock === "function" &&
        document.fullscreenElement
      ) {
        window.screen.orientation.lock("landscape").catch(() => {});
      }
    } catch (e) {
      // Ignore orientation lock silently without prompting system permissions
    }
  };

  // Unlock orientation when exiting fullscreen
  const unlockOrientation = () => {
    try {
      if (window.screen && window.screen.orientation && typeof window.screen.orientation.unlock === "function") {
        window.screen.orientation.unlock();
      }
    } catch (e) {
      // Ignore if unlock fails
    }
  };

  // Toggle Fullscreen Mode
  const toggleFullscreen = () => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container) return;

    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      let playPromise = null;
      if (container.requestFullscreen) {
        playPromise = container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        playPromise = container.webkitRequestFullscreen();
      } else if (container.msRequestFullscreen) {
        playPromise = container.msRequestFullscreen();
      } else if (video && video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      }

      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => lockLandscapeOrientation())
          .catch(() => lockLandscapeOrientation());
      } else {
        lockLandscapeOrientation();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      unlockOrientation();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen change events & auto-lock/unlock orientation
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(isFull);
      if (isFull) {
        lockLandscapeOrientation();
      } else {
        unlockOrientation();
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Format Time Helper (MM:SS)
  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === 0) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins < 10 ? "0" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleClose = () => {
    unlockOrientation();
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (onClose) onClose();
  };

  // Auto-close modal when YouTube iframe video completes
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data && (data.info === 0 || (data.info && data.info.playerState === 0))) {
          handleClose();
        }
      } catch (e) {
        // ignore non-json messages
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isOpen) return null;

  return (
    <div className="film-modal-backdrop" onClick={handleUnmute}>
      <div
        className="film-modal-container"
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Top Bar Header */}
        <div className={`film-modal-header ${showControls ? "active" : ""}`}>
          <div className="film-modal-title">
            <FaFilm style={{ color: "#ff9d0b", fontSize: "1.3rem" }} />
            <h3>{videoTitle}</h3>
          </div>
          <button
            className="film-modal-close-btn"
            onClick={handleClose}
            title="Close Movie Popup"
            aria-label="Close"
          >
            <FaXmark />
          </button>
        </div>

        {/* Video Player Box */}
        <div
          className="film-video-wrapper"
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => isPlaying && setShowControls(false)}
          onClick={triggerShowControls}
          onTouchStart={triggerShowControls}
        >
          {youtubeEmbedUrl ? (
            <iframe
              src={youtubeEmbedUrl}
              title={videoTitle}
              style={{ width: "100%", height: "100%", border: 0 }}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <>
              {/* Loading Spinner for Buffering */}
              {isBuffering && !videoError && (
                <div className="film-loading-overlay">
                  <div className="film-spinner"></div>
                  <span>Loading Stream...</span>
                </div>
              )}

              {/* Video Load Error Overlay */}
              {videoError && (
                <div className="film-loading-overlay" style={{ flexDirection: "column", gap: "10px", textAlign: "center", padding: "20px" }}>
                  <div style={{ fontSize: "2rem", color: "#ff4d4d" }}>⚠️</div>
                  <span style={{ fontSize: "1.1rem", fontWeight: "600", color: "#fff" }}>Unable to stream video</span>
                  <p style={{ fontSize: "0.85rem", color: "#ccc", maxWidth: "320px", margin: "0 auto" }}>
                    The video stream is unavailable or failed to load. Please check your connection or try again later.
                  </p>
                  <button className="btn film-btn-primary mt-2 px-4" onClick={handleClose}>Close Window</button>
                </div>
              )}

              {/* Audio Unmute Banner Fallback if Browser Restricted Initial Audio */}
              {autoplayBlocked && (
                <button
                  className="unmute-banner"
                  onClick={(e) => { e.stopPropagation(); handleUnmute(); }}
                  title="Enable Audio"
                  aria-label="Tap to enable audio"
                >
                  <FaVolumeHigh /> <span>Tap for Audio</span>
                </button>
              )}

              {/* Big Center Play / Pause Button Overlay */}
              {(!isPlaying || showControls) && !isBuffering && (
                <button
                  className={`yt-center-play-btn ${!isPlaying ? "visible" : ""}`}
                  onClick={(e) => { e.stopPropagation(); togglePlay(); triggerShowControls(); }}
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? <FaPause /> : <FaPlay style={{ marginLeft: "4px" }} />}
                </button>
              )}

              <video
                ref={videoRef}
                className="film-video-element"
                preload="auto"
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleClose}
                onClick={(e) => { e.stopPropagation(); togglePlay(); triggerShowControls(); }}
                playsInline
                disableRemotePlayback
                controlsList="nodownload noremoteplayback"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
              />
            </>
          )}

          {/* YouTube Style Control Bar Overlay */}
          <div
            className={`film-controls-bar yt-style-controls ${showControls ? "active" : ""}`}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {/* YouTube Red Progress Slider */}
            <div className="film-progress-container">
              <input
                type="range"
                className="film-progress-bar yt-progress-bar"
                min="0"
                max={duration || 100}
                step="0.1"
                value={currentTime}
                onChange={handleSeek}
                style={{
                  background: `linear-gradient(to right, #ff0000 0%, #ff0000 ${
                    duration ? (currentTime / duration) * 100 : 0
                  }%, rgba(255, 255, 255, 0.25) ${
                    duration ? (currentTime / duration) * 100 : 0
                  }%, rgba(255, 255, 255, 0.25) 100%)`
                }}
              />
            </div>

            <div className="film-controls-row">
              <div className="film-controls-left">
                {/* Play/Pause Button */}
                <button
                  className="film-ctrl-btn"
                  onClick={togglePlay}
                  title={isPlaying ? "Pause (k)" : "Play (k)"}
                >
                  {isPlaying ? <FaPause /> : <FaPlay />}
                </button>

                {/* Skip -10s */}
                <button
                  className="film-ctrl-btn"
                  onClick={() => skipTime(-10)}
                  title="Rewind 10 seconds"
                >
                  <FaRotateLeft style={{ fontSize: "1.05rem" }} />
                </button>

                {/* Skip +10s */}
                <button
                  className="film-ctrl-btn"
                  onClick={() => skipTime(10)}
                  title="Forward 10 seconds"
                >
                  <FaRotateRight style={{ fontSize: "1.05rem" }} />
                </button>

                {/* Volume / Mute Button */}
                <button
                  className="film-ctrl-btn"
                  onClick={toggleMute}
                  title={isMuted ? "Unmute (m)" : "Mute (m)"}
                >
                  {isMuted ? <FaVolumeXmark style={{ color: "#ff0000" }} /> : <FaVolumeHigh />}
                </button>

                {/* Volume Slider */}
                <input
                  type="range"
                  className="film-volume-slider"
                  min="0"
                  max="1"
                  step="0.05"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  style={{ width: "65px", accentColor: "#ff0000", cursor: "pointer" }}
                />

                {/* Time Display */}
                <span className="film-time-display">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>


              <div className="film-controls-right" style={{ position: "relative" }}>
                {/* Playback Speed Menu Popup */}
                {showSpeedMenu && (
                  <div className="yt-speed-popup-menu">
                    <div className="yt-speed-header">Playback Speed</div>
                    {[0.5, 1, 1.25, 1.5, 2].map((speed) => (
                      <button
                        key={speed}
                        className={`yt-speed-item ${playbackSpeed === speed ? "active" : ""}`}
                        onClick={() => changeSpeed(speed)}
                      >
                        {speed === 1 ? "Normal" : `${speed}x`}
                      </button>
                    ))}
                  </div>
                )}

                {/* Video Quality Menu Popup */}
                {showQualityMenu && (
                  <div className="yt-speed-popup-menu">
                    <div className="yt-speed-header">Video Quality</div>
                    <button
                      className={`yt-speed-item ${selectedQuality === -1 ? "active" : ""}`}
                      onClick={() => changeQuality(-1)}
                    >
                      Auto (ABR)
                    </button>
                    {qualityLevels.length > 0 ? (
                      qualityLevels.map((lvl) => (
                        <button
                          key={lvl.id}
                          className={`yt-speed-item ${selectedQuality === lvl.id ? "active" : ""}`}
                          onClick={() => changeQuality(lvl.id)}
                        >
                          {getQualityLabel(lvl.height)}
                        </button>
                      ))
                    ) : (
                      <>
                        <button
                          className={`yt-speed-item ${selectedQuality === "2160p" ? "active" : ""}`}
                          onClick={() => changeQuality("2160p")}
                        >
                          2160p (4K Ultra HD)
                        </button>
                        <button
                          className={`yt-speed-item ${selectedQuality === "1080p" ? "active" : ""}`}
                          onClick={() => changeQuality("1080p")}
                        >
                          1080p (Full HD)
                        </button>
                        <button
                          className={`yt-speed-item ${selectedQuality === "720p" ? "active" : ""}`}
                          onClick={() => changeQuality("720p")}
                        >
                          720p (HD)
                        </button>
                        <button
                          className={`yt-speed-item ${selectedQuality === "480p" ? "active" : ""}`}
                          onClick={() => changeQuality("480p")}
                        >
                          480p
                        </button>
                        <button
                          className={`yt-speed-item ${selectedQuality === "360p" ? "active" : ""}`}
                          onClick={() => changeQuality("360p")}
                        >
                          360p
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Speed Settings Button */}
                <button
                  className="film-ctrl-btn"
                  onClick={() => {
                    setShowSpeedMenu(!showSpeedMenu);
                    setShowQualityMenu(false);
                  }}
                  title="Playback Speed"
                >
                  <FaGear style={{ fontSize: "1.1rem" }} />
                </button>

                {/* HD Quality Badge - Clickable Quality Selector */}
                <button
                  className="yt-hd-badge"
                  onClick={() => {
                    setShowQualityMenu(!showQualityMenu);
                    setShowSpeedMenu(false);
                  }}
                  title="Click to Change Quality"
                  aria-label="Change Quality"
                >
                  {selectedQuality === -1
                    ? "Auto HD"
                    : typeof selectedQuality === "string"
                    ? `${selectedQuality}`
                    : qualityLevels.find((l) => l.id === selectedQuality)
                    ? `${qualityLevels.find((l) => l.id === selectedQuality).height}p HD`
                    : `${selectedQuality}`}
                </button>

                {/* Fullscreen Button */}
                <button
                  className="film-ctrl-btn"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? "Exit Fullscreen (f)" : "Full Screen (f)"}
                >
                  {isFullscreen ? <FaCompress /> : <FaExpand />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortFilmModal;
